import { useState, type FormEvent } from "react";
import { api } from "../api.js";
import { useAuth } from "../auth.js";
import { DesktopDownloadCta } from "../DesktopDownloadCta.js";
import { HeroBrand, LocaleToggle, PasswordField, ThemeToggle, useLocale } from "../ui.js";
import type { AuthView } from "../App.js";

export function LoginPage({ onNavigate }: { onNavigate: (v: AuthView) => void }) {
  const { tr } = useLocale();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [twoFactor, setTwoFactor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await api.login(email.trim(), password, twoFactor ? code.trim() : undefined);
    setBusy(false);
    if (res.ok && res.data?.session && res.data.account) {
      setSession(res.data.account);
      return;
    }
    const denial = res.data?.denialCode ?? res.denialCode;
    // Privileged accounts must complete an emailed 2FA code.
    if (res.data?.twoFactorRequired || denial === "two_factor_required") {
      setTwoFactor(true);
      setError(null);
      return;
    }
    if (denial === "two_factor_invalid") setError(tr("invalidCreds"));
    else if (denial === "pending_approval") setError(tr("pendingApproval"));
    else if (denial === "account_blocked") setError(tr("blocked"));
    else if (denial === "invalid_credentials") setError(tr("invalidCreds"));
    else setError(tr("genericError"));
  }

  return (
    <main className="ss-login">
      <HeroBrand />
      <div className="ss-login-form-wrap">
        <form className="ss-login-card" onSubmit={submit}>
          <div className="ss-login-toprow">
            <ThemeToggle />
            <LocaleToggle />
          </div>
          <h2>{tr("login")}</h2>
          <p className="sub">{tr("loginSub")}</p>
          <div className="ss-field">
            <label htmlFor="email">{tr("email")}</label>
            <input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <PasswordField id="password" label={tr("password")} value={password} onChange={setPassword} autoComplete="current-password" />
          {twoFactor ? (
            <>
              <div className="msg info">
                <strong>{tr("twoFactorTitle")}</strong>
                <div>{tr("twoFactorSub")}</div>
              </div>
              <div className="ss-field">
                <label htmlFor="twofa">{tr("code")}</label>
                <input
                  id="twofa"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </>
          ) : null}
          {error ? <div className="msg error">{error}</div> : null}
          <button className="ss-login-submit" type="submit" disabled={busy}>
            {twoFactor ? tr("verifyLogin") : tr("login")}
          </button>
          <div className="btn-row">
            <button type="button" className="ss-btn-ghost" onClick={() => onNavigate("signup")}>
              {tr("toSignup")}
            </button>
            <button type="button" className="ss-btn-ghost" onClick={() => onNavigate("forgot")}>
              {tr("forgot")}
            </button>
          </div>
          <DesktopDownloadCta />
        </form>
      </div>
    </main>
  );
}
